'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { endOfDay, endOfMonth, format, startOfDay } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import { es } from 'date-fns/locale'
import { DateRange } from 'react-day-picker'
import {
  Calendar as CalendarIcon,
  MapPin,
  Users,
  TrendingUp,
  DollarSign,
  RefreshCw,
  Store,
  ChevronDown,
  ChevronUp,
  Bug,
  Database,
  Cloud,
  Activity,
  Globe,
  Repeat,
  MessageSquare,
  Trash2,
  X,
} from 'lucide-react'

import { useSalesReport } from '@/hooks/useSalesReport'
import { useToast } from '@/hooks/useToast'
import { cn, formatCurrency } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Skeleton } from '@/components/ui/skeleton'
import { Header } from '../layout/Header'
import { FunctionsService } from '@/services/functions.service'

const DEFAULT_RANGE: DateRange = {
  from: startOfDay(new Date()),
  to: endOfDay(new Date()),
}

const PR_TIMEZONE = 'America/Puerto_Rico'

// Mapeo mejorado de categorías de origen con palabras clave
const ORIGIN_CATEGORIES = {
  'Instagram': [
    // Variaciones exactas
    'instagram',
    'ig',
    'insta',
    // Frases comunes
    'redes ig',
    'redes intagram',
    'por instagram',
    'a través de instagram',
    'nos conoció por ig',
    'nos vio a través de instagram',
    'nos conoce por instagram',
    'por el ig',
    'conoció por ig',
    'vio en ig',
    'encontró en ig',
  ],
  'Caminando por el Mall': [
    // Variaciones de caminando
    'caminando',
    'pasando',
    'paseando',
    'explorando',
    // Menciones al mall/máquina
    'mall',
    'por el mall',
    'caminando por el mall',
    'explorando el mall',
    'caminando el mall',
    'por la maquina',
    'la maquina',
    'maquina',
  ],
  'Recomendación': [
    // Recomendaciones directas
    'recomendación',
    'recomendacion',
    'por recomendación',
    // Referencias de personas
    'por un amigo',
    'referencia',
    'por referencia',
    'referencia de un amigo',
    'amistades la recomendaron',
    'conoce a',
    'por su hijo',
    'compro la novia',
    'su novia',
    'su esposa',
    'su hermano',
    'familiar',
    'amigo',
    'conocido',
  ],
  'Ya conocía la tienda': [
    'ya conocía',
    'ya conocia',
    'conocía la tienda',
    'conocia la tienda',
    'cliente frecuente',
    'cliente regular',
  ],
  'Redes Sociales (General)': [
    'redes sociales',
    'redes',
    'red social',
    'facebook',
    'fb',
    'tiktok',
    'twitter',
    'x.com',
  ],
  'Venta Interna': [
    'VENTA_INTERNA',
    'venta interna',
    'empleado',
    'staff',
  ],
  'Sin Especificar': [],
}

// Función mejorada para categorizar el origen con búsqueda de palabras clave
// Ahora prioriza "Cliente Recurrente" si tiene más de 1 orden O si la nota dice "cliente"
function categorizeOrigin(note: string | null | undefined, ordersCount?: number): string {
  // Normalizar nota para búsquedas
  const normalizedNote = note ? note.trim().toLowerCase() : ''

  // PRIORIDAD 1: Cliente Recurrente por nota "cliente"
  const clienteKeywords = [
    'cliente',
    'cliente antiguo',
    'cliente recurrente',
    'cliente frecuente',
    'cliente regular',
    'ya es cliente',
    'cliente de la tienda',
  ]

  for (const keyword of clienteKeywords) {
    if (normalizedNote.includes(keyword)) {
      return 'Cliente Recurrente'
    }
  }

  // PRIORIDAD 2: Cliente Recurrente (más de 1 orden histórica)
  if (ordersCount && ordersCount > 1) {
    return 'Cliente Recurrente'
  }

  // PRIORIDAD 3: Si no hay nota, retornar Sin Especificar
  if (!note || note.trim() === '') return 'Sin Especificar'

  // PRIORIDAD 4: Categorizar por nota
  // Buscar coincidencias en orden de prioridad
  for (const [category, keywords] of Object.entries(ORIGIN_CATEGORIES)) {
    for (const keyword of keywords) {
      const normalizedKeyword = keyword.toLowerCase()

      // Buscar la palabra clave en cualquier parte de la nota
      if (normalizedNote.includes(normalizedKeyword)) {
        return category
      }
    }
  }

  // Si no hay coincidencia, categorizar como "Otros"
  return 'Otros'
}

interface OriginData {
  category: string
  count: number
  totalSales: number
  percentage: number
  orders: any[]
}

export function SalesOriginDashboard() {
  const { toast } = useToast()

  const [dateRange, setDateRange] = useState<DateRange>(DEFAULT_RANGE)
  const { report, loading, error, getSalesReport } = useSalesReport()
  const [selectedPreset, setSelectedPreset] = useState<'today' | 'month-to-date' | 'full-month' | 'custom'>('today')
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set())
  const [showDebug, setShowDebug] = useState(false)

  const currentFrom = dateRange?.from ?? DEFAULT_RANGE.from
  const currentTo = dateRange?.to ?? DEFAULT_RANGE.to

  const handleGenerateReport = useCallback(
    async (range: DateRange | undefined, forceShopify = false) => {
      if (!range?.from || !range?.to) return
      await getSalesReport(startOfDay(range.from), endOfDay(range.to), forceShopify)
    },
    [getSalesReport]
  )

  const handleForceShopify = useCallback(async () => {
    toast({
      title: '🌐 Forzando Shopify API',
      description: 'Obteniendo datos frescos desde Shopify...',
    })
    await handleGenerateReport(dateRange, true)
  }, [dateRange, handleGenerateReport, toast])

  useEffect(() => {
    handleGenerateReport(dateRange)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handlePresetChange = useCallback(
    (preset: 'today' | 'month-to-date' | 'full-month') => {
      const now = new Date()
      let nextRange: DateRange

      if (preset === 'today') {
        nextRange = { from: startOfDay(now), to: endOfDay(now) }
      } else if (preset === 'month-to-date') {
        const monthStart = startOfDay(new Date(now.getFullYear(), now.getMonth(), 1))
        nextRange = { from: monthStart, to: endOfDay(now) }
      } else {
        const monthStart = startOfDay(new Date(now.getFullYear(), now.getMonth(), 1))
        nextRange = { from: monthStart, to: endOfDay(endOfMonth(now)) }
      }

      setSelectedPreset(preset)
      setDateRange(nextRange)
      handleGenerateReport(nextRange)
    },
    [handleGenerateReport]
  )

  const handleDateChange = useCallback((range: DateRange | undefined) => {
    if (!range?.from) return

    const next: DateRange = {
      from: startOfDay(range.from),
      to: range.to ? endOfDay(range.to) : endOfDay(range.from),
    }

    setSelectedPreset('custom')
    setDateRange(next)
  }, [])

  const handleGenerateClick = useCallback(() => {
    handleGenerateReport(dateRange)
  }, [dateRange, handleGenerateReport])

  const handleClearCache = useCallback(async () => {
    try {
      const response = await FunctionsService.clearCache(
        dateRange?.from && dateRange?.to ? { from: dateRange.from, to: dateRange.to } : undefined
      )

      if (response.error) {
        toast({
          title: 'Error al limpiar caché',
          description: response.error,
          variant: 'destructive',
        })
      } else {
        toast({
          title: 'Caché limpiado',
          description: response.data?.message ?? 'La próxima consulta utilizará información actualizada.',
        })
      }
    } catch (error: any) {
      toast({
        title: 'Error al limpiar caché',
        description: error?.message ?? 'No se pudo limpiar la caché.',
        variant: 'destructive',
      })
    }
  }, [dateRange, toast])

  const toggleCategory = useCallback((category: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev)
      if (next.has(category)) {
        next.delete(category)
      } else {
        next.add(category)
      }
      return next
    })
  }, [])

  // Procesar datos de origen
  const originData = useMemo((): OriginData[] => {
    if (!report?.orders?.length) return []

    const categoryMap = new Map<string, { count: number; totalSales: number; orders: any[] }>()

    report.orders.forEach((order: any) => {
      // Validar que la orden tenga datos mínimos
      if (!order || !order.id) {
        console.warn('Orden inválida o sin ID:', order)
        return
      }

      const origin = categorizeOrigin(
        order.note || order.customer?.note,
        order.customer?.orders_count
      )

      // Soportar ambos formatos: camelCase (totalPrice) y snake_case (total_price)
      const price = order.totalPrice ?? order.total_price

      const totalPrice = typeof price === 'string'
        ? parseFloat(price)
        : (typeof price === 'number' ? price : 0)

      if (!categoryMap.has(origin)) {
        categoryMap.set(origin, { count: 0, totalSales: 0, orders: [] })
      }

      const data = categoryMap.get(origin)!
      data.count += 1
      data.totalSales += totalPrice
      data.orders.push(order)
    })

    const total = report.orders.length
    const results: OriginData[] = []

    categoryMap.forEach((data, category) => {
      results.push({
        category,
        count: data.count,
        totalSales: data.totalSales,
        percentage: (data.count / total) * 100,
        orders: data.orders,
      })
    })

    return results.sort((a, b) => b.count - a.count)
  }, [report?.orders])

  const totalOrders = useMemo(() => {
    return originData.reduce((sum, item) => sum + item.count, 0)
  }, [originData])

  const totalRevenue = useMemo(() => {
    return originData.reduce((sum, item) => sum + item.totalSales, 0)
  }, [originData])

  const topOrigin = useMemo(() => {
    return originData.length > 0 ? originData[0] : null
  }, [originData])

  const summaryCards = useMemo(() => {
    return [
      {
        title: 'Total de Órdenes',
        value: totalOrders.toLocaleString('es-ES'),
        icon: Store,
      },
      {
        title: 'Ingresos Totales',
        value: formatCurrency(totalRevenue),
        icon: DollarSign,
      },
      {
        title: 'Origen Principal',
        value: topOrigin?.category || 'N/A',
        icon: TrendingUp,
      },
      {
        title: 'Categorías',
        value: originData.length.toString(),
        icon: MapPin,
      },
    ]
  }, [totalOrders, totalRevenue, topOrigin, originData.length])

  return (
    <div className="mx-auto w-full max-w-[1600px] px-4 py-6 sm:px-6 lg:px-8 space-y-4">
      <Header />

      {/* Control Card */}
      <Card className="border-white/10 bg-white/5 text-white backdrop-blur">
        <CardContent className="pt-4 space-y-3">
          {/* Row 1: Presets + calendar */}
          <div className="flex overflow-x-auto no-scrollbar gap-2 pb-1 items-center">
            <button
              className={cn(
                'flex-shrink-0 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors',
                selectedPreset === 'today'
                  ? 'bg-blue-500 text-white border-blue-500'
                  : 'bg-white/10 text-white/70 border-white/15 hover:bg-white/15 hover:text-white'
              )}
              onClick={() => handlePresetChange('today')}
            >
              Hoy
            </button>
            <button
              className={cn(
                'flex-shrink-0 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors',
                selectedPreset === 'month-to-date'
                  ? 'bg-blue-500 text-white border-blue-500'
                  : 'bg-white/10 text-white/70 border-white/15 hover:bg-white/15 hover:text-white'
              )}
              onClick={() => handlePresetChange('month-to-date')}
            >
              Días del mes
            </button>
            <button
              className={cn(
                'flex-shrink-0 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors',
                selectedPreset === 'full-month'
                  ? 'bg-blue-500 text-white border-blue-500'
                  : 'bg-white/10 text-white/70 border-white/15 hover:bg-white/15 hover:text-white'
              )}
              onClick={() => handlePresetChange('full-month')}
            >
              Mes completo
            </button>

            <Popover>
              <PopoverTrigger asChild>
                <button
                  className={cn(
                    'flex-shrink-0 flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors',
                    selectedPreset === 'custom'
                      ? 'bg-blue-500 text-white border-blue-500'
                      : 'bg-white/10 text-white/70 border-white/15 hover:bg-white/15 hover:text-white'
                  )}
                >
                  <CalendarIcon className="h-3.5 w-3.5" />
                  {currentFrom && currentTo
                    ? `${format(currentFrom, 'P', { locale: es })} - ${format(currentTo, 'P', { locale: es })}`
                    : 'Rango'}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto bg-gray-950/95 p-0 text-white" align="start">
                <Calendar
                  mode="range"
                  numberOfMonths={2}
                  locale={es}
                  selected={dateRange}
                  defaultMonth={currentFrom}
                  onSelect={handleDateChange}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Row 2: Actions */}
          <div className="flex overflow-x-auto no-scrollbar gap-2 pb-1 items-center">
            <Button
              size="sm"
              className="flex-shrink-0 flex items-center gap-2 bg-emerald-500 text-white hover:bg-emerald-600"
              onClick={handleGenerateClick}
              disabled={loading}
            >
              <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
              Generar
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex-shrink-0 flex items-center gap-2 bg-red-500/20 text-red-200 hover:bg-red-500/30 border-red-500/30"
              onClick={handleClearCache}
              disabled={loading}
            >
              <Trash2 className="h-4 w-4" />
              Limpiar caché
            </Button>
            <Button
              variant="outline"
              size="sm"
              className={cn(
                'flex-shrink-0 flex items-center gap-2 border-white/10 transition-colors',
                showDebug
                  ? 'bg-yellow-500/20 text-yellow-200 hover:bg-yellow-500/30 border-yellow-500/30'
                  : 'bg-white/10 text-white hover:bg-white/20'
              )}
              onClick={() => setShowDebug(!showDebug)}
            >
              <Bug className="h-4 w-4" />
              Debug
            </Button>
          </div>

          {error && (
            <div className="rounded-md border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200">
              {error}
            </div>
          )}
        </CardContent>
      </Card>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {summaryCards.map((card) => (
          <Card key={card.title} className="border-white/10 bg-white/5 text-white backdrop-blur">
            <CardHeader className="pb-1 px-4 pt-4">
              <CardDescription className="flex items-center gap-1.5 text-xs text-white/60">
                <card.icon className="h-3.5 w-3.5" />
                {card.title}
              </CardDescription>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              {loading ? (
                <Skeleton className="h-7 w-24 bg-white/10" />
              ) : (
                <p className="text-xl font-bold tabular-nums text-white">{card.value}</p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Origin Breakdown */}
      <Card className="border-white/10 bg-white/5 text-white backdrop-blur overflow-hidden">
        <CardHeader className="px-5 py-4">
          <CardTitle className="flex items-center gap-2 text-sm font-semibold text-white">
            <MapPin className="h-4 w-4 text-white/50" />
            Desglose por Origen
          </CardTitle>
          <CardDescription className="text-xs text-white/50">
            Distribución de ventas según el origen reportado
          </CardDescription>
        </CardHeader>
        <CardContent className="px-5 pb-5">
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-16 w-full bg-white/10" />
              ))}
            </div>
          ) : originData.length > 0 ? (
            <div className="space-y-3">
              {originData.map((item) => {
                const isExpanded = expandedCategories.has(item.category)
                return (
                  <div key={item.category} className="rounded-lg border border-white/10 bg-white/5">
                    <button
                      onClick={() => toggleCategory(item.category)}
                      className="w-full text-left px-4 py-3"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-white">{item.category}</span>
                            {isExpanded
                              ? <ChevronUp className="h-3.5 w-3.5 flex-shrink-0 text-white/40" />
                              : <ChevronDown className="h-3.5 w-3.5 flex-shrink-0 text-white/40" />
                            }
                          </div>
                          <div className="mt-0.5 flex items-center gap-3 text-xs text-white/50">
                            <span className="flex items-center gap-1">
                              <Users className="h-3.5 w-3.5" />
                              {item.count} órdenes
                            </span>
                            <span className="flex items-center gap-1">
                              <DollarSign className="h-3.5 w-3.5" />
                              {formatCurrency(item.totalSales)}
                            </span>
                          </div>
                        </div>
                        <span className="flex-shrink-0 text-lg font-bold tabular-nums text-white">
                          {item.percentage.toFixed(1)}%
                        </span>
                      </div>
                      <div className="mt-2.5 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
                        <div
                          className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all"
                          style={{ width: `${item.percentage}%` }}
                        />
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="border-t border-white/10 px-4 py-3 space-y-2">
                        <p className="text-xs font-medium uppercase tracking-wide text-white/50">
                          Órdenes ({item.orders.length})
                        </p>
                        <div className="max-h-80 space-y-1.5 overflow-y-auto">
                          {item.orders.map((order: any) => {
                            let formattedDate = 'Fecha no disponible'
                            const dateField = order.date || order.created_at
                            if (dateField) {
                              try {
                                const date = new Date(dateField)
                                if (!isNaN(date.getTime())) {
                                  formattedDate = formatInTimeZone(date, PR_TIMEZONE, 'PPp', { locale: es })
                                }
                              } catch (_) { /* ignore */ }
                            }

                            const orderPrice = (() => {
                              const price = order.totalPrice || order.total_price
                              return typeof price === 'string'
                                ? parseFloat(price)
                                : (typeof price === 'number' ? price : 0)
                            })()

                            return (
                              <div
                                key={order.id}
                                className="rounded-md border border-white/5 bg-white/5 px-3 py-2.5 hover:bg-white/10 transition-colors"
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div className="min-w-0 flex-1">
                                    <p className="text-xs font-medium text-white">
                                      {order.orderNumber || order.name || `#${order.id}`}
                                    </p>
                                    <p className="text-xs text-white/40">{formattedDate}</p>
                                    {order.customer && (
                                      <div className="mt-1 space-y-0.5">
                                        {(order.customer.first_name || order.customer.last_name) && (
                                          <p className="text-xs text-white/60">
                                            {order.customer.first_name} {order.customer.last_name}
                                          </p>
                                        )}
                                        {order.customer.orders_count > 1 && (
                                          <p className="flex items-center gap-1 text-xs text-purple-400">
                                            <Repeat className="h-3 w-3" />
                                            Cliente Recurrente ({order.customer.orders_count} órdenes)
                                          </p>
                                        )}
                                        {order.customer.note && (
                                          <p className="flex items-center gap-1 text-xs italic text-blue-400">
                                            <MessageSquare className="h-3 w-3 flex-shrink-0" />
                                            "{order.customer.note}"
                                          </p>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex-shrink-0 text-right">
                                    <p className="text-xs font-semibold text-emerald-400">
                                      {formatCurrency(orderPrice)}
                                    </p>
                                    {(order.note || order.customer?.note) && (
                                      <p className="max-w-[100px] truncate text-xs text-white/40">
                                        {order.note || order.customer?.note}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          ) : (
            <p className="py-8 text-center text-sm text-white/40">No hay datos para mostrar</p>
          )}
        </CardContent>
      </Card>

      {/* Debug Panel */}
      {showDebug && (
        <Card className="border-yellow-500/20 bg-yellow-500/5 text-white backdrop-blur">
          <CardHeader className="px-5 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bug className="h-4 w-4 text-yellow-400" />
                <CardTitle className="text-sm font-semibold text-yellow-100">Panel de Debug</CardTitle>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDebug(false)}
                className="h-7 w-7 p-0 text-yellow-300 hover:text-yellow-100 hover:bg-yellow-500/10"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="px-5 pb-5 space-y-4">
            <div className="rounded-lg border border-white/10 bg-white/5 p-4">
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-white/60">Fuente de datos</h3>
              <div className="flex items-center gap-2 text-sm">
                {(report?.metadata as any)?.cacheSource === 'shopify_api_forced' && (
                  <><Globe className="h-4 w-4 text-blue-400" /><span className="text-blue-400">Shopify API (Forzado)</span></>
                )}
                {(report?.metadata as any)?.cacheSource === 'shopify_api' && (
                  <><Globe className="h-4 w-4 text-blue-400" /><span className="text-blue-400">Shopify API</span></>
                )}
                {(report?.metadata as any)?.cacheSource === 'hot_cache' && (
                  <><Database className="h-4 w-4 text-emerald-400" /><span className="text-emerald-400">Firestore Hot Cache</span></>
                )}
                {report?.metadata?.cacheSource === 'memory' && (
                  <><Activity className="h-4 w-4 text-purple-400" /><span className="text-purple-400">Memory Cache</span></>
                )}
                {!report?.metadata?.cacheSource && (
                  <span className="text-white/40">No hay datos cargados</span>
                )}
              </div>
              {(report?.metadata as any)?.responseTime && (
                <p className="mt-1 text-xs text-white/50">
                  Tiempo de respuesta: {(report?.metadata as any)?.responseTime}ms
                </p>
              )}
            </div>

            <div className="rounded-lg border border-white/10 bg-white/5 p-4">
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-white/60">Información de órdenes</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="text-white/70">Total: <span className="font-semibold text-white">{report?.orders?.length || 0}</span></div>
                {report?.orders && report.orders.length > 0 && (
                  <>
                    <div className="text-white/70">Con customer: <span className="font-semibold text-emerald-400">{report.orders.filter((o: any) => o.customer).length}</span></div>
                    <div className="text-white/70">Con nota: <span className="font-semibold text-blue-400">{report.orders.filter((o: any) => o.customer?.note).length}</span></div>
                    <div className="text-white/70">Con fecha: <span className="font-semibold text-purple-400">{report.orders.filter((o: any) => o.date).length}</span></div>
                    <div className="text-white/70">Con total: <span className="font-semibold text-yellow-400">{report.orders.filter((o: any) => o.totalPrice && o.totalPrice > 0).length}</span></div>
                  </>
                )}
              </div>
            </div>

            {report?.orders && report.orders.length > 0 && (
              <div className="rounded-lg border border-white/10 bg-white/5 p-4">
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-white/60">Muestra primera orden</h3>
                <pre className="max-h-48 overflow-auto rounded bg-black/30 p-3 text-xs text-white/60">
                  {JSON.stringify({
                    id: (report.orders[0] as any).id,
                    orderNumber: (report.orders[0] as any).orderNumber || (report.orders[0] as any).order_number,
                    date: report.orders[0].date,
                    totalPrice: (report.orders[0] as any).totalPrice || (report.orders[0] as any).total_price,
                    customer: (report.orders[0] as any).customer,
                  }, null, 2)}
                </pre>
              </div>
            )}

            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleForceShopify}
                disabled={loading}
                className="flex-1 border border-blue-500/20 bg-blue-500/20 text-blue-200 hover:bg-blue-500/30"
              >
                <Cloud className="mr-2 h-4 w-4" />
                Forzar Shopify API
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => console.log('Full Report:', report)}
                className="flex-1 border-purple-500/20 bg-purple-500/20 text-purple-200 hover:bg-purple-500/30"
              >
                Log a Consola
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

export default SalesOriginDashboard
